import React, { createContext, useCallback, useContext, useState } from 'react';

export interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
}

interface NotificationContextType {
  notifications: Notification[];
  notify: (type: Notification['type'], message: string) => void;
  success: (message: string) => void;
  error: (message: string) => void;
  warning: (message: string) => void;
  info: (message: string) => void;
  dismiss: (id: string) => void;
}

const NotificationContext = createContext<NotificationContextType | null>(null);

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const dismiss = useCallback((id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const notify = useCallback((type: Notification['type'], message: string) => {
    const id = Date.now().toString() + Math.random().toString(36).slice(2);
    setNotifications(prev => [...prev, { id, type, message }]);

    setTimeout(() => {
      dismiss(id);
    }, type === 'error' ? 5000 : 3000);
  }, [dismiss]);

  const success = useCallback((message: string) => notify('success', message), [notify]);
  const error = useCallback((message: string) => notify('error', message), [notify]);
  const warning = useCallback((message: string) => notify('warning', message), [notify]);
  const info = useCallback((message: string) => notify('info', message), [notify]);

  return (
    <NotificationContext.Provider value={{ notifications, notify, success, error, warning, info, dismiss }}>
      {children}
      {/* Toast container */}
      {notifications.length > 0 && (
        <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-2 max-w-sm">
          {notifications.map((notification) => (
            <div
              key={notification.id}
              className={`px-4 py-3 rounded-lg shadow-lg text-white text-sm flex items-start gap-2 animate-slide-in ${
                notification.type === 'success' ? 'bg-green-600' :
                notification.type === 'error' ? 'bg-red-600' :
                notification.type === 'warning' ? 'bg-yellow-600' :
                'bg-blue-600'
              }`}
            >
              <span className="flex-1">{notification.message}</span>
              <button
                onClick={() => dismiss(notification.id)}
                className="text-white/80 hover:text-white shrink-0 text-lg leading-none"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
    </NotificationContext.Provider>
  );
};

export function useNotification(): NotificationContextType {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    throw new Error('useNotification must be used within NotificationProvider');
  }
  return ctx;
}
